// src/screens/seller/notifications/SellerNotifications.js
import React, { useEffect, useState, useContext } from "react";
import {
  FaShoppingBag,
  FaTimesCircle,
  FaStar,
  FaBell,
  FaTicketAlt,
  FaCheckCircle,
  FaWallet,
  FaUndo,
} from "react-icons/fa";
import { FaComments } from "react-icons/fa";
import axios from "axios";
import MyContext from "../../../contexts/AppContext";
import SellerMasterLayout from "../../../layouts/seller/SellerMasterLayout";
import "../../../assets/css/seller/screens/SellerNotifications.css";
import { useTheme } from "../../../contexts/ThemeContext";

const getConfig = (title) => {
  const t = (title || "").toLowerCase();


  if (t.includes("customization confirmed"))
    return {
      icon: <FaCheckCircle />,
      className: "s-resolved",
      label: "Customization Confirmed",
    };

  if (t.includes("customization declined"))
    return {
      icon: <FaTimesCircle />,
      className: "s-rejected",
      label: "Customization Declined",
    };

    if (t.includes("new message"))
    return {
      icon: <FaComments />,
      className: "s-general",
      label: "New Message",
    };

  // Return resolved/approved — green
  if (
    t.includes("return") &&
    (t.includes("approved") || t.includes("resolved"))
  )
    return {
      icon: <FaUndo />,
      className: "s-resolved",
      label: "Return Resolved",
    };

  // Return rejected — red
  if (t.includes("return") && t.includes("rejected"))
    return {
      icon: <FaUndo />,
      className: "s-rejected",
      label: "Return Rejected",
    };

  // Return request (new / pending) — purple
  if (t.includes("return"))
    return {
      icon: <FaUndo />,
      className: "s-return",
      label: "Return Request",
    };

  // Shop approved — green
  if (
    t.includes("shop approved") ||
    (t.includes("shop") && t.includes("approved"))
  )
    return {
      icon: <FaCheckCircle />,
      className: "s-resolved",
      label: "Shop Approved",
    };

  // Shop rejected — red
  if (
    t.includes("shop rejected") ||
    (t.includes("shop") && t.includes("rejected"))
  )
    return {
      icon: <FaTimesCircle />,
      className: "s-rejected",
      label: "Shop Rejected",
    };

  // Product approved — green
  if (
    t.includes("product approved") ||
    (t.includes("product") && t.includes("approved"))
  )
    return {
      icon: <FaCheckCircle />,
      className: "s-resolved",
      label: "Product Approved",
    };

  // Product rejected — red
  if (
    t.includes("product rejected") ||
    (t.includes("product") && t.includes("rejected"))
  )
    return {
      icon: <FaTimesCircle />,
      className: "s-rejected",
      label: "Product Rejected",
    };

  // TopUp approved — green
  if (
    t.includes("topup approved") ||
    (t.includes("topup") && t.includes("approved"))
  )
    return {
      icon: <FaWallet />,
      className: "s-resolved",
      label: "TopUp Approved",
    };

  // TopUp rejected — red
  if (
    t.includes("topup rejected") ||
    (t.includes("topup") && t.includes("rejected"))
  )
    return {
      icon: <FaWallet />,
      className: "s-rejected",
      label: "TopUp Rejected",
    };

  // TopUp general
  if (t.includes("topup"))
    return { icon: <FaWallet />, className: "s-general", label: "TopUp" };

  if (t.includes("resolved"))
    return {
      icon: <FaTicketAlt />,
      className: "s-resolved",
      label: "Resolved",
    };
  if (t.includes("rejected"))
    return {
      icon: <FaTicketAlt />,
      className: "s-rejected",
      label: "Rejected",
    };
  if (t.includes("ticket") || t.includes("support"))
    return { icon: <FaTicketAlt />, className: "s-ticket", label: "Complaint" };
  if (t.includes("cancel"))
    return {
      icon: <FaTimesCircle />,
      className: "s-cancelled",
      label: "Cancelled",
    };
  if (t.includes("review") || t.includes("rating"))
    return { icon: <FaStar />, className: "s-review", label: "Review" };
  if (t.includes("order") || t.includes("new"))
    return { icon: <FaShoppingBag />, className: "s-neworder", label: "Order" };
  return { icon: <FaBell />, className: "s-general", label: "General" };
};

const formatTime = (dateStr) => {
  const now = new Date();
  const date = new Date(dateStr);
  const mins = Math.floor((now - date) / 60000);
  const hours = Math.floor(mins / 60);
  const days = Math.floor(hours / 24);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins} minute${mins > 1 ? "s" : ""} ago`;
  if (hours < 24) return `${hours} hour${hours > 1 ? "s" : ""} ago`;
  if (days === 1) return "Yesterday";
  if (days < 7) return `${days} days ago`;
  return date.toLocaleDateString("en-PK", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const FILTERS = [
  { key: "All", label: "All" },
  { key: "unread", label: "Unread" },
  { key: "Shop", label: "Shop" },
  { key: "Product", label: "Products" },
  { key: "TopUp", label: "TopUp" },
  { key: "Order", label: "Orders" },
  { key: "Return", label: "Returns" },
  { key: "Cancelled", label: "Cancelled" },
  { key: "Complaint", label: "Complaints" },
  { key: "Review", label: "Reviews" },
];

const COMPLAINT_LABELS = ["Complaint", "Resolved", "Rejected"];
const SHOP_LABELS = ["Shop Approved", "Shop Rejected"];
const PRODUCT_LABELS = [
  "Product Approved",
  "Product Rejected",
  "Customization Confirmed",
  "Customization Declined",
  "New Message",
];
const TOPUP_LABELS = ["TopUp Approved", "TopUp Rejected", "TopUp"];
const RETURN_LABELS = ["Return Request", "Return Resolved", "Return Rejected"];
const SellerNotifications = () => {
  const { baseUrl, clearUnread ,sellerAuthToken } = useContext(MyContext);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeFilter, setActiveFilter] = useState("All");

  const fetchNotifications = () => {
    setLoading(true);
    const seller = JSON.parse(localStorage.getItem("loggedInSeller"));
    const fd = new FormData();
    fd.append("chseller_id", seller?.id);

    axios
      .post(`${baseUrl}/seller/order/getSellerNotifications`, fd, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${sellerAuthToken}`,
        },
      })
      .then((res) => {
        const sorted = (res.data.notifications || []).sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at),
        );
        setNotifications(sorted);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  useEffect(() => {
    fetchNotifications();
    clearUnread("notifications");
  }, []);

  const unreadCount = notifications.filter((n) => n.status === "unread").length;
  const { isDark } = useTheme();
  const markAllRead = () => {
    const seller = JSON.parse(localStorage.getItem("loggedInSeller"));
    const fd = new FormData();
    fd.append("chseller_id", seller?.id);
    axios.post(`${baseUrl}/seller/order/markAllRead`, fd,
      {
        headers: {
          "Content-Type": "multipart/form-data",
           Authorization: `Bearer ${sellerAuthToken}`,
        },
      },
    ).then(() => {
      setNotifications((prev) => prev.map((n) => ({ ...n, status: "read" })));
    });
  };

  const getFiltered = () => {
    if (activeFilter === "All") return notifications;
    if (activeFilter === "unread")
      return notifications.filter((n) => n.status === "unread");
    if (activeFilter === "Complaint")
      return notifications.filter((n) =>
        COMPLAINT_LABELS.includes(getConfig(n.title).label),
      );
    if (activeFilter === "Shop")
      return notifications.filter((n) =>
        SHOP_LABELS.includes(getConfig(n.title).label),
      );
    if (activeFilter === "Product")
      return notifications.filter((n) =>
        PRODUCT_LABELS.includes(getConfig(n.title).label),
      );
    if (activeFilter === "TopUp")
      return notifications.filter((n) =>
        TOPUP_LABELS.includes(getConfig(n.title).label),
      );
      if (activeFilter === "Return")
      return notifications.filter((n) =>
        RETURN_LABELS.includes(getConfig(n.title).label),
      );
    return notifications.filter(
      (n) => getConfig(n.title).label === activeFilter,
    );
  };

  const filtered = getFiltered();

  const getCount = (key) => {
    if (key === "All") return 0;
    if (key === "unread") return unreadCount;
    if (key === "Complaint")
      return notifications.filter(
        (n) =>
          COMPLAINT_LABELS.includes(getConfig(n.title).label) &&
          n.status === "unread",
      ).length;
    if (key === "Shop")
      return notifications.filter(
        (n) =>
          SHOP_LABELS.includes(getConfig(n.title).label) &&
          n.status === "unread",
      ).length;
    if (key === "Product")
      return notifications.filter(
        (n) =>
          PRODUCT_LABELS.includes(getConfig(n.title).label) &&
          n.status === "unread",
      ).length;
    if (key === "TopUp")
      return notifications.filter(
        (n) =>
          TOPUP_LABELS.includes(getConfig(n.title).label) &&
          n.status === "unread",
      ).length;
        if (key === "Return")
      return notifications.filter(
        (n) =>
          RETURN_LABELS.includes(getConfig(n.title).label) &&
          n.status === "unread",
      ).length;
    return notifications.filter(
      (n) => getConfig(n.title).label === key && n.status === "unread",
    ).length;
  };

  return (
    <SellerMasterLayout>
      <div className={`sn-shell ${isDark ? "dark-mode" : ""}`}>
        <main className="sn-main">
          <section className="sn-card-shell">
            {/* ── Header ── */}
            <div className="sn-page-header">
              <div className="sn-page-title-row">
                <h1 className="sn-page-title">Notifications</h1>
                {unreadCount > 0 && (
                  <span className="sn-unread-badge">{unreadCount} unread</span>
                )}
              </div>
              <button className="sn-btn-markread" onClick={markAllRead}>
                ✓ Mark all as read
              </button>
            </div>

            {/* ── Filters ── */}
            <div className="sn-filters">
              {FILTERS.map((f) => {
                const count = getCount(f.key);
                return (
                  <button
                    key={f.key}
                    className={`sn-filter-tab ${activeFilter === f.key ? "active" : ""}`}
                    onClick={() => setActiveFilter(f.key)}
                  >
                    {f.label}
                    {count > 0 && (
                      <span className="sn-filter-count">{count}</span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* ── Loading ── */}
            {loading && (
              <div className="sn-loading">
                <div className="sn-spinner" />
                <p>Loading notifications...</p>
              </div>
            )}

            {/* ── Empty ── */}
            {!loading && filtered.length === 0 && (
              <div className="sn-empty">
                <FaBell size={48} />
                <p>No notifications here yet.</p>
              </div>
            )}

            {/* ── List ── */}
            {!loading && filtered.length > 0 && (
              <div className="sn-list">
                {filtered.map((n) => {
                  const config = getConfig(n.title);
                  return (
                    <div
                      key={n.id}
                      className={`sn-card ${config.className} ${n.status === "unread" ? "unread" : ""}`}
                    >
                      {n.status === "unread" && <span className="sn-dot" />}
                      <div className="sn-card-icon">{config.icon}</div>
                      <div className="sn-card-body">
                        <h4 className="sn-card-title">{n.title}</h4>
                        <p className="sn-card-desc">{n.description}</p>
                        <div className="sn-card-meta">
                          <span
                            className={`sn-card-pill ${config.className}-pill`}
                          >
                            {config.label}
                          </span>
                          <span className="sn-card-time">
                            {new Date(n.created_at).toLocaleString("en-PK")}
                          </span>
                          <span className="sn-card-ago">
                            {formatTime(n.created_at)}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </main>
      </div>
    </SellerMasterLayout>
  );
};

export default SellerNotifications;